/*
 * The band summary lines — "3 fencers in 7 events" and "3 tournaments, 7 events".
 *
 * WHY A MODULE AND NOT TWO INLINE `useMemo`s. Upcoming and Past both need both
 * axes, so this is four call sites over two shapes. The counting itself is
 * trivial; what is not trivial is the DEDUPLICATION, and getting it wrong is
 * invisible: a fencer registered for six events at one tournament must count once
 * on the tournament band, and a tournament appearing under three of a fencer's
 * events must count once on the fencer band. Written once, tested once
 * (decision 326).
 *
 * COMPOSED FROM PLURALISED FRAGMENTS rather than assembled with a `+`. Each count
 * gets its own plural key and the sentence that joins them is itself a template,
 * so a locale with different plural categories — or a different word order — has
 * every piece it needs. Direction §7's rule is that display text is never
 * assembled from parts the translator cannot see; two `t()` calls dropped into a
 * third translated template satisfies that, string concatenation does not.
 */

import { list } from "./api";
import { peopleOf, personKey, type FencerIdentity, type PersonRef } from "./regroup";

/**
 * The minimum shape needed to count. Deliberately narrower than the payloads.
 *
 * Counting DISTINCT PEOPLE is the whole job here, so it keys on the same
 * identity the fencer axis bands by — a resolved fencer where there is one, the
 * alias otherwise (decision 345). Keying on `fencer_id` alone would have counted
 * every unresolved row as the same undefined person. A TEAM's row counts each
 * of its tracked members, as the fencer axis bands it (decision 714).
 */
type CountableFencer = PersonRef & Pick<FencerIdentity, "team_members">;

interface CountableEvent<F extends CountableFencer> {
  fencers: F[] | null;
}

/**
 * A tournament band's counts: how many distinct people, across how many events.
 *
 * The events are already this tournament's, so their count needs no set — but the
 * fencers do, because the same person appears in every event they entered.
 */
export function tournamentCounts<F extends CountableFencer, E extends CountableEvent<F>>(
  events: E[] | null | undefined,
): { fencers: number; events: number } {
  const seen = new Set<string>();
  const all = list(events);

  for (const event of all) {
    for (const f of list(event.fencers)) {
      for (const p of peopleOf({ ...f, name: f.name ?? "" })) seen.add(personKey(p));
    }
  }

  return { fencers: seen.size, events: all.length };
}

/**
 * A fencer band's counts: how many tournaments, across how many events.
 *
 * Both come straight from the transposed shape — `byFencer` has already bucketed
 * this fencer's events under their tournaments — so the tournaments are the
 * buckets and the events are their contents. No set is needed here precisely
 * BECAUSE the transpose did the grouping; counting the raw payload instead would
 * need one, which is the trap this function exists to keep out of the screens.
 */
export function fencerCounts<E>(
  buckets: { events: E[] }[] | null | undefined,
): { tournaments: number; events: number } {
  const all = list(buckets);
  return {
    tournaments: all.length,
    events: all.reduce((n, b) => n + b.events.length, 0),
  };
}
